import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { applyAntdTheme } from './antdTheme'
import { STORAGE_KEY, applyTheme, isThemeId, readStoredTheme, resolveTheme } from './themes'

const DARK_QUERY = '(prefers-color-scheme: dark)'

const ThemeContext = createContext({
  theme: 'system',
  resolvedTheme: 'light',
  setTheme: () => {},
})

function prefersDark() {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => readStoredTheme())
  const [osDark, setOsDark] = useState(prefersDark)

  // Only System cares about the OS setting, but listening always is cheap
  // and means switching to System shows the right result immediately.
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined
    const media = window.matchMedia(DARK_QUERY)
    const onChange = (e) => setOsDark(e.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const resolvedTheme = resolveTheme(theme, osDark)

  useEffect(() => {
    const root = document.documentElement
    // One frame without transitions so every surface switches together.
    root.setAttribute('data-theme-switching', 'true')
    applyTheme(resolvedTheme, root)
    applyAntdTheme(resolvedTheme)
    const frame = requestAnimationFrame(() => root.removeAttribute('data-theme-switching'))
    return () => cancelAnimationFrame(frame)
  }, [resolvedTheme])

  const setTheme = useCallback((next) => {
    if (!isThemeId(next)) return
    setThemeState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Private mode or blocked storage: the theme still applies for this visit.
    }
  }, [])

  const value = useMemo(() => ({ theme, resolvedTheme, setTheme }), [theme, resolvedTheme, setTheme])

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export const useTheme = () => useContext(ThemeContext)
